import {Box, Card, Title} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {parseCookies} from 'nookies';
import type {FC} from 'react';
import {MdWork} from 'react-icons/md';
import type {z} from 'zod';
import {
  DEPARTMENT_CATEGORY_NAME_MAX_LENGTH,
  DEPARTMENT_NAME_MAX_LENGTH,
  ReportTagType,
} from '../../../../core/entities/reports/constants';
import type {ReportSchema} from '../../../../core/entities/reports/schema';
import {isInternReport} from '../../../../core/entities/shared/isInternReport';
import {convertStringToReportType} from '../../../../core/factories/reports/convertStringToReportType';
import {shortenString} from '../../../../core/factories/reports/shortenString';
import {useReports} from '../../../../core/repositories/reportsRepository';
import {validateCookie} from '../../../../core/usecases/auth/checkOauthTokenExistence';
import {ClipButton} from '../../../../shared/components/ClipButton';
import {CommonLink} from '../../../../shared/components/CommonLink';
import {NoLoginAlertModal} from '../../../../shared/components/NoLoginAlertModal';
import {convertGraduationYearToLabel} from '../../../../shared/services/convertGraduatioinYearToLabel';
import {formatDate} from '../../../../shared/services/formatDate';
import {useMediaQuery} from '../../../../shared/services/useMediaQuery';
import {ReportTypeLabel} from '../../../reports/components/Container/Presenter/ReportTypeLabel';
import {useHandleClip} from '../../../reports/usecase/useHandleClip';

type CardProps = {
  report: z.infer<typeof ReportSchema>;
};

type Props = {
  companyId: number;
  referenceIds: number[];
  reportType: string;
  isPC: boolean;
};

const ReferenceCard: FC<CardProps> = ({report}) => {
  const cookies = parseCookies();
  const {isValid} = validateCookie(cookies);

  const {isPC} = useMediaQuery();
  const handleReportClick = () => {
    if (report.type === 'entrysheet') {
      if (isPC) {
        window.open(`/passed_es/${report.id}`, '_blank');
      } else {
        location.href = `/passed_es/${report.id}`;
      }
    } else if (isPC) {
      window.open(`/selection_reports/${report.id}`, '_blank');
    } else {
      location.href = `/selection_reports/${report.id}`;
    }
  };

  const [opened, {open, close}] = useDisclosure(false);

  const {handleClip, clipCountUpdated, isClipped} = useHandleClip({
    isLogin: isValid,
    reportId: report.id,
    reportType: report.type,
    clipCount: report.clipsCount,
    clipped: report.clipped,
  });

  const handleClipClick = async () => {
    if (isValid) {
      return handleClip();
    }

    open();
  };

  return (
    <>
      <Card
        className="flex cursor-pointer flex-col p-0 shadow-card"
        onClick={handleReportClick}
      >
        <div className="flex h-[105px] flex-col p-16">
          <div className="flex flex-row justify-between items-start">
            <div className="flex flex-row items-center gap-x-8">
              <ReportTypeLabel type={convertStringToReportType(report.type)} />
              <div className="py-2 flex items-center justify-center rounded-full border-[1px] px-8 text-xs text-black-2 h-20">
                {convertGraduationYearToLabel(report.reporterGraduationYear)}
              </div>
              {isInternReport(report.type) ? (
                <div className="py-2 flex items-center justify-center rounded-full border-[1px] px-8 text-xs text-black-2 h-20">
                  {ReportTagType.INTERN}
                </div>
              ) : (
                <>
                  {report.reporterGetOffer ? (
                    <div className="py-2 flex items-center justify-center rounded-full border-[1px] px-8 text-xs text-black-2 h-20">
                      {ReportTagType.OFFERED}
                    </div>
                  ) : (
                    <div className="py-2 flex items-center justify-center rounded-full border-[1px] px-8 text-xs text-black-2 h-20">
                      {ReportTagType.REJECTED}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex flex-row items-center gap-x-8">
              <ClipButton
                count={`${clipCountUpdated}`}
                isClipped={isClipped}
                handleUpdate={async () => handleClipClick()}
              />
            </div>
          </div>
          <div className="mb-8 mt-[-8px] flex flex-row justify-between text-base font-bold text-black-1">
            {shortenString(report.departmentName, DEPARTMENT_NAME_MAX_LENGTH)}
          </div>
          <div className="flex flex-row justify-between">
            <div className="flex flex-row items-center justify-center gap-x-4 text-sm text-black-2">
              <MdWork />
              {shortenString(
                report.departmentCategory.name,
                DEPARTMENT_CATEGORY_NAME_MAX_LENGTH,
              )}
            </div>
            <div className="text-sm text-black-2">
              {formatDate(report.publishedAt)}
            </div>
          </div>
        </div>
        <div className="flex h-[60px] flex-col overflow-hidden bg-background-1 px-16 py-12 text-sm text-black-1">
          <div
            style={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              WebkitLineClamp: 2,
              maxHeight: '2 * 1.2em',
            }}
          >
            {report.excerpt.length > 0 ? report.excerpt : ''}
          </div>
        </div>
      </Card>
      <NoLoginAlertModal opened={opened} onClose={close} />
    </>
  );
};

export const ReferenceBox: FC<Props> = ({
  companyId,
  referenceIds,
  reportType,
  isPC,
}) => {
  const types =
    reportType === 'selection'
      ? [reportType]
      : ['summer', 'winter', 'spring', 'autumn'];
  const {data} = useReports({
    queryParams: {
      companyIDs: companyId,
      types,
      departmentMasterIDs: [],
      limit: 4,
      page: 1,
      selectionReportIDs: referenceIds,
    },
  });
  const departmentMasterIDs = data?.ok
    ? [
        ...new Set(
          data.value.reports
            .map((report) => report.departmentCategory.id)
            .filter((id) => id !== 0),
        ),
      ]
    : [];
  return (
    <Box className="bg-white w-full p-16">
      <Box id="report-reference" className="flex w-full justify-between">
        <Title order={2} className="text-black-1 text-lg font-bold p-16">
          参考にした体験記
        </Title>
        <CommonLink
          href={`/company/${companyId}/experiences?department=${departmentMasterIDs.join(
            '%2C',
          )}&type=${types.join('%2C')}`}
          target="_blank"
          className="text-primary-2 p-16 pc:text-base text-sm cursor-pointer hover:underline focus:outline-none whitespace-nowrap"
        >
          もっと見る
        </CommonLink>
      </Box>
      <Box className="grid pc:grid-cols-2 p-8 gap-16">
        {data?.ok &&
          data.value.reports
            .slice(0, isPC ? data.value.reports.length : 2) // SPの場合は2つまで
            .map((report) => <ReferenceCard key={report.id} report={report} />)}
      </Box>
    </Box>
  );
};
